import { useContext } from 'react';
import { get2DKeyHandler } from 't-lib-ui/structure';
import { AppState } from '../context/general';
import FeedIcon from './FeedIcon';
import { formatFeedType, formatShortFrequency, nameColor } from '../utils/formatting';
import type { HomeFeed } from '../schema-copy/api-schema';
import './DiscoverTile.css';

const keyHandler = get2DKeyHandler((el: any) => el.click()) as any;

/**
 * Large feed tile of the Home page's Discover rows and the category ("See all") page: icon, title
 * and "type · frequency" line. Its size comes from the --discover-tile-size of the surrounding
 * .discoverTiles container. Part of the 2D keyboard navigation (flow2d).
 * A real link to the feed page, so middle-click / right-click "Open in new tab" work; plain clicks
 * stay in-app via onOpen.
 */
const DiscoverTile: React.FC<{ feed: HomeFeed; onOpen: () => void }> = ({ feed, onOpen }) => {
  // The content type is dropped on narrow screens (.discoverTileType), where the tiles are too
  // small for both.
  const type = formatFeedType(feed.types);
  const rest = `${formatShortFrequency(feed.postFrequency)}${feed.paid ? ' $' : ''}`.trim();
  const { feedEngine } = useContext(AppState);
  return (
    <a
      href={`/a/feed/${feed.id}`}
      tabIndex={0}
      className="discoverTile flowFocussable flow2d"
      title={feed.title}
      draggable={false}
      onClick={e => {
        if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        // The reader header can show title and icon before the feed's info has been fetched.
        feedEngine.rememberFeedHeader(feed.id, { title: feed.title, iconUrl: feed.iconUrl, circularIcon: feed.circularIcon });
        onOpen();
      }}
      onKeyDown={keyHandler}
    >
      <FeedIcon
        className="discoverTileIcon"
        src={feed.iconUrl}
        src2x={feed.iconUrl2x}
        circular={feed.circularIcon}
        fallback={<span className="discoverTileIcon discoverTileIconFallback" style={{ background: nameColor(feed.title) }}>{feed.title.slice(0, 2)}</span>}
      />
      <span className="discoverTileName">{feed.title}</span>
      {(type || rest) && (
        <span className="discoverTileMeta">
          {type && <span className="discoverTileType">{type}{rest && ' · '}</span>}
          {rest}
        </span>
      )}
    </a>
  );
};

export default DiscoverTile;

/** Empty stand-in with a DiscoverTile's exact geometry (icon box plus name and meta lines), shown
 *  while a row's feeds load so the row's height is reserved and nothing shifts when they arrive. */
export const DiscoverTilePlaceholder: React.FC = () => (
  <span className="discoverTile discoverTilePlaceholder" aria-hidden>
    <span className="discoverTileIcon" />
    <span className="discoverTileName">{' '}</span>
    <span className="discoverTileMeta">{' '}</span>
  </span>
);
